import {
  Button,
  ButtonGroup,
  ConfirmModal,
  Icon,
  LinkButton,
} from '@webkom/lego-bricks';
import {
  Contact,
  Copy,
  FilePieChart,
  Pencil,
  Trash2,
  TriangleAlert,
  UserCog,
} from 'lucide-react';
import moment from 'moment-timezone';
import { useState } from 'react';
import { navigate } from 'vike/client/router';
import AnnouncementInLine from '~/components/AnnouncementInLine';
import { TextInput } from '~/components/Form';
import { deleteEvent } from '~/redux/actions/EventActions';
import { useAppDispatch } from '~/redux/hooks';
import { EventType } from '~/redux/models/Event';
import type { EntityId } from '@reduxjs/toolkit';
import type { ActionGrant } from 'app/models';
import type { CompleteEvent } from '~/redux/models/Event';

type ButtonProps = {
  eventId: EntityId;
  title: string;
};

const DeleteButton = ({ eventId, title }: ButtonProps) => {
  const [eventName, setEventName] = useState('');
  const [show, setShow] = useState(false);

  const dispatch = useAppDispatch();

  return (
    <>
      {!show ? (
        <Button danger onPress={() => setShow(true)}>
          <Icon iconNode={<Trash2 />} size={19} />
          Slett arrangement
        </Button>
      ) : (
        <>
          <label htmlFor="delete-event">
            Skriv inn navnet på arrangementet du vil slette
          </label>
          <TextInput
            id="delete-event"
            type="text"
            prefixIconNode={<TriangleAlert />}
            placeholder="Arrangementnavn"
            onChange={(e) => setEventName(e.target.value)}
          />

          {eventName === title && (
            <ConfirmModal
              title="Slett arrangement"
              message="Er du helt sikker på at du vil slette dette arrangementet?"
              onConfirm={() =>
                dispatch(deleteEvent(eventId)).then(() => {
                  navigate('/events');
                })
              }
            >
              {({ openConfirmModal }) => (
                <Button onPress={openConfirmModal} danger>
                  <Icon iconNode={<Trash2 />} size={19} />
                  Slett
                </Button>
              )}
            </ConfirmModal>
          )}
        </>
      )}
    </>
  );
};

type Props = {
  event: Pick<
    CompleteEvent,
    'id' | 'slug' | 'title' | 'startTime' | 'survey' | 'eventType'
  >;
  actionGrant: ActionGrant;
};

const Admin = ({ actionGrant, event }: Props) => {
  const canEdit = actionGrant.includes('edit');
  const canDelete = actionGrant.includes('delete');
  const canAdministrate = actionGrant.includes('administrate');
  const isInterestEvent = event.eventType === EventType.INTEREST_EVENT;
  const showRegisterButton =
    canAdministrate &&
    Math.abs(
      moment.duration(moment(event.startTime).diff(moment.now())).get('days'),
    ) < 1;

  return (
    (canEdit || canDelete) && (
      <div>
        <h3>Admin</h3>

        <ButtonGroup vertical>
          {showRegisterButton && (
            <LinkButton success href={`/events/${event.id}/abascanner`}>
              <Icon iconNode={<Contact />} size={19} />
              Registrer oppmøte
            </LinkButton>
          )}

          {canAdministrate && (
            <LinkButton href={`/events/${event.id}/administrate/attendees`}>
              <Icon iconNode={<UserCog />} size={19} />
              Administrer
            </LinkButton>
          )}

          {canEdit && (
            <LinkButton
              href={
                isInterestEvent
                  ? `/events/interest/${event.slug}/edit`
                  : `/events/${event.slug}/edit`
              }
            >
              <Icon iconNode={<Pencil />} size={19} />
              Rediger
            </LinkButton>
          )}

          <AnnouncementInLine event={event} />

          {!isInterestEvent &&
            (event.survey ? (
              <LinkButton href={`/surveys/${event.survey}`}>
                <Icon iconNode={<FilePieChart />} size={19} />
                Gå til spørreundersøkelse
              </LinkButton>
            ) : (
              <LinkButton href={`/surveys/new/?event=${event.id}`}>
                <Icon iconNode={<FilePieChart />} size={19} />
                Lag spørreundersøkelse
              </LinkButton>
            ))}

          {!isInterestEvent && (
            <LinkButton href="/events/new" state={{ id: event.id }}>
              <Icon iconNode={<Copy />} size={19} />
              Lag kopi av arrangement
            </LinkButton>
          )}

          {canDelete && <DeleteButton eventId={event.id} title={event.title} />}
        </ButtonGroup>
      </div>
    )
  );
};

export default Admin;
